import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InsightsIcon from '@mui/icons-material/Insights';
import { tokens } from '../../global/theme/tokens';
import { capacityRanking, capacityTotals } from './capacityFilter';

const MiniStat = ({ label, value, color }) => (
  <Box className="flex flex-col" sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{
        opacity: 0.55,
        fontSize: '0.66rem',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ color, fontWeight: 700, fontSize: '0.95rem' }}>
      {value}
    </Typography>
  </Box>
);

// the number of the first column, with its caption below
const TotalStat = ({ label, value, color }) => (
  <Box className="flex flex-col">
    <Typography
      sx={{ color, fontWeight: 700, fontSize: '2rem', lineHeight: 1.1 }}
    >
      {value}
    </Typography>
    <Typography fontSize="12px" className="opacity-60">
      {label}
    </Typography>
  </Box>
);

/**
 * One bar per employee, longest first. A single measure per chart, so the bars
 * share one colour and the name on the left carries the identity - no legend
 * and no colour per person, which also keeps every employee visible.
 */
const CapacityBars = ({ title, rows, color, unit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  // the longest bar fills the track, the rest are read against it
  const max = rows.reduce((peak, row) => Math.max(peak, row.value), 0);

  return (
    <Box className="flex flex-col gap-2 min-w-0">
      <Typography fontSize="12px" className="opacity-60" noWrap>
        {title}
      </Typography>

      {rows.length ? (
        <Box
          className="flex flex-col gap-1 min-w-0 pr-1"
          sx={{ maxHeight: '190px', overflowY: 'auto' }}
        >
          {rows.map((row) => (
            <Box
              key={row.id}
              className="flex items-center gap-2 min-w-0 rounded"
              title={`${row.label}: ${row.value} ${unit}`}
              sx={{
                paddingY: '1px',
                ':hover': { backgroundColor: `${colors.text}08` },
              }}
            >
              <Typography
                fontSize="12px"
                noWrap
                sx={{ width: '35%', minWidth: 0 }}
              >
                {row.label}
              </Typography>

              {/* the track shows how far this employee is from the busiest one */}
              <Box
                className="grow rounded-full overflow-hidden"
                sx={{ height: '10px', backgroundColor: `${colors.text}10` }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: max ? `${(row.value / max) * 100}%` : 0,
                    backgroundColor: color,
                    borderRadius: '4px',
                    transition: 'width 300ms',
                  }}
                />
              </Box>

              <Typography
                fontSize="12px"
                fontWeight={600}
                className="monospace shrink-0"
                sx={{ minWidth: '28px', textAlign: 'right' }}
              >
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography fontSize="13px" className="opacity-50 py-6 text-center">
          {t('No data available')}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Head of the capacity view: what the employees below have open in total and
 * how it is spread over them. Everything comes from the capacities response, so
 * the numbers follow the filter of the list.
 */
const EmployeeCapacityOverview = ({ entries = [] }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const totals = useMemo(() => capacityTotals(entries), [entries]);

  const taskRows = useMemo(() => capacityRanking(entries, 'open'), [entries]);

  const pointRows = useMemo(
    () => capacityRanking(entries, 'points'),
    [entries],
  );

  return (
    <Box
      className="shrink-0"
      sx={{
        background: `linear-gradient(135deg, ${colors.glassFrom}, ${colors.glassTo})`,
        border: `1px solid ${colors.selected}`,
        backdropFilter: 'blur(12px)',
        borderRadius: '16px',
        padding: { xs: 1, md: 1.25 },
        display: 'flex',
        flexDirection: 'column',
        gap: isOpen ? 1.75 : 0,
        transition: 'gap 0.2s ease',
      }}
    >
      <Box
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 cursor-pointer"
        sx={{
          paddingX: 0.5,
          paddingY: 0.25,
          borderRadius: '8px',
          ':hover': { backgroundColor: `${colors.selected}40` },
        }}
      >
        <InsightsIcon sx={{ fontSize: '18px', color: colors.info }} />

        <Box
          className="flex flex-col sm:flex-row sm:items-baseline gap-x-2"
          sx={{ flex: '1 1 0%', minWidth: 0 }}
        >
          <Typography fontSize="16px" fontWeight={600} noWrap>
            {t('Employee Capacity')}
          </Typography>
          <Typography fontSize="11px" noWrap className="opacity-60">
            {`${totals.employees} ${t('Employees')}`}
          </Typography>
        </Box>

        {/* closed the counters take the place of the charts */}
        {!isOpen && (
          <Box className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 w-full sm:w-auto shrink-0 pr-1 order-last sm:order-none">
            <MiniStat label={t('Open')} value={totals.open} />
            <MiniStat label={t('Points')} value={totals.points} />
          </Box>
        )}

        {/* the whole header toggles, so the button must not toggle it back */}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          <KeyboardArrowRightIcon
            sx={{
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              lg: 'minmax(140px, 0.6fr) 1fr 1fr',
            },
            alignItems: 'center',
            paddingX: 0.5,
          }}
        >
          <Box className="flex flex-row lg:flex-col gap-6 lg:gap-3">
            <TotalStat label={t('Open tasks')} value={totals.open} />
            <TotalStat
              label={t('Points')}
              value={totals.points}
              color={colors.info}
            />
          </Box>

          <CapacityBars
            title={t('Open tasks per employee')}
            rows={taskRows}
            color={colors.info}
            unit={t('Tasks')}
          />

          <CapacityBars
            title={t('Points per employee')}
            rows={pointRows}
            color={colors.colorPool[4]}
            unit={t('Points')}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default EmployeeCapacityOverview;
